import {
  Box,
  Button,
  Container,
  Fieldset,
  Flex,
  Image,
  Input,
  Link as ChakraLink,
  Skeleton,
  Text
} from '@chakra-ui/react';
import logo from '@/assets/icon/logo.svg';
import { useForm, UseFormRegisterReturn } from 'react-hook-form';
import { fetchAPI } from '@/api/fetch';
import {
  apiGetInvitation,
  apiInvitationSignUp,
  GetInvitationResponse,
  LoginResponse
} from '@/api/services';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import useAuth from '@/store/auth';

// Constants
const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 30,
  PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/
};

const PASSWORD_STRENGTH_LEVELS = {
  WEAK: 25,
  FAIR: 50,
  GOOD: 75
};

// Types
type FormValues = {
  email: string;
  agencyName: string;
  name: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
};

type PasswordStrengthIndicatorProps = {
  strength: number;
};

type FormInputProps = {
  label: string;
  error?: { message?: string };
  register: UseFormRegisterReturn<string>;
  rightElement?: React.ReactNode;
} & Omit<React.ComponentProps<typeof Input>, 'register'>;

// Components
const PasswordStrengthIndicator = ({ strength }: PasswordStrengthIndicatorProps) => {
  const getStrengthLabel = (strength: number): string => {
    if (strength <= PASSWORD_STRENGTH_LEVELS.WEAK) return 'Weak';
    if (strength <= PASSWORD_STRENGTH_LEVELS.FAIR) return 'Fair';
    if (strength <= PASSWORD_STRENGTH_LEVELS.GOOD) return 'Good';
    return 'Strong';
  };

  const getStrengthColor = (strength: number): string => {
    if (strength <= PASSWORD_STRENGTH_LEVELS.WEAK) return 'error.700';
    if (strength <= PASSWORD_STRENGTH_LEVELS.FAIR) return 'warning.700';
    if (strength <= PASSWORD_STRENGTH_LEVELS.GOOD) return 'info.700';
    return 'success.700';
  };

  return (
    <Box mt="8px">
      <Box w="100%" h="4px" bg="neutrals.100" borderRadius="4px" overflow="hidden">
        <Box
          h="100%"
          w={`${strength}%`}
          bg={getStrengthColor(strength)}
          transition="all 0.3s ease"
        />
      </Box>
      <Text fontSize="12px" color={getStrengthColor(strength)} mt="4px">
        {getStrengthLabel(strength)}
      </Text>
    </Box>
  );
};

const FormInput = ({ label, error, register, rightElement, ...props }: FormInputProps) => (
  <Box mt="20px">
    <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
      {label}
    </Text>
    <Flex alignItems="center" position="relative">
      <Input
        fontSize="16px"
        height="48px"
        borderRadius="8px"
        _placeholder={{ color: 'neutrals.300' }}
        _focus={{
          boxShadow: 'none',
          outline: 'none',
          borderColor: error ? 'error.700' : 'neutrals.400'
        }}
        borderColor={error ? 'error.700' : 'neutrals.300'}
        variant="outline"
        p="12px 16px"
        {...register}
        {...props}
      />
      {rightElement}
    </Flex>
    {error?.message && (
      <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
        {error.message}
      </Text>
    )}
  </Box>
);

// Utility functions
const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/[a-z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  return strength;
};

const LoadingSkeleton = () => (
  <Container
    maxW="100%"
    minH="100dvh"
    w="100%"
    h="100%"
    p={0}
    bg="background.primary"
    display="flex"
    flexDir="column"
  >
    <header style={{ position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
      <Flex p="32px 50px">
        <Skeleton width="145px" height="40px" />
      </Flex>
    </header>
    <Flex flexGrow={1} mx="auto" overflowY="auto" alignItems="center" justifyContent="center">
      <Fieldset.Root
        borderRadius="10px"
        w="450px"
        h="725px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box>
          <Skeleton height="36px" width="200px" mb="20px" />

          {/* Email field skeleton */}
          <Skeleton height="16px" width="80px" mb="10px" />
          <Skeleton height="48px" width="100%" mb="20px" />

          {/* Full Name field skeleton */}
          <Skeleton height="16px" width="100px" mb="10px" />
          <Skeleton height="48px" width="100%" mb="20px" />

          {/* Password field skeleton */}
          <Skeleton height="16px" width="90px" mb="10px" />
          <Skeleton height="48px" width="100%" mb="8px" />
          <Skeleton height="4px" width="100%" mb="4px" />
          <Skeleton height="12px" width="60px" mb="20px" />

          {/* Confirm Password field skeleton */}
          <Skeleton height="16px" width="140px" mb="10px" />
          <Skeleton height="48px" width="100%" mb="20px" />

          {/* Terms checkbox skeleton */}
          <Skeleton height="24px" width="300px" mb="20px" />

          {/* Submit button skeleton */}
          <Skeleton height="60px" width="100%" />
        </Box>
      </Fieldset.Root>
    </Flex>
  </Container>
);

export default function InvitationSignUp() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    shouldFocusError: false,
    mode: 'onChange',
    defaultValues: {
      email: '',
      agencyName: '',
      name: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false
    }
  });

  useEffect(() => {
    const fetchInvitation = async (token: string) => {
      try {
        setIsLoading(true);
        const response = await fetchAPI<GetInvitationResponse>({
          request: () => apiGetInvitation(token)
        });

        if (response.success) {
          setValue('email', response.data.email);
          setValue('agencyName', response.data.agency_name);
        } else {
          setSubmitError(response.message || 'Failed to fetch invitation');
          navigate('/login');
        }
      } catch (error) {
        setSubmitError('Failed to fetch invitation: ' + error);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInvitation(token);
    } else {
      navigate('/login');
    }
  }, [token, navigate, setValue]);

  async function onSubmit(values: FormValues) {
    if (!token) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetchAPI<LoginResponse>({
        request: () =>
          apiInvitationSignUp(token, {
            name: values.name,
            password: values.password
          })
      });

      if (response.success) {
        const { account, access_token: accessToken, refresh_token: refreshToken } = response.data;
        login(account, accessToken, refreshToken);
        navigate('/');
      } else {
        console.error('Failed to create account: ', response.message);
        setSubmitError('Failed to create account. Please try again.');
      }
    } catch {
      setSubmitError('Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  const password = watch('password');
  const agreeToTerms = watch('agreeToTerms');

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Container
      maxW="100%"
      minH="100dvh"
      w="100%"
      h="100%"
      p={0}
      bg="background.primary"
      display="flex"
      flexDir="column"
    >
      <header style={{ position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
        <Flex p="32px 50px">
          <Image
            src={logo}
            alt="logo"
            width="145px"
            onClick={() => navigate('/')}
            cursor="pointer"
          />
        </Flex>
      </header>
      <Flex flexGrow={1} mx="auto" overflowY="auto" alignItems="center" justifyContent="center">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset.Root
            borderRadius="10px"
            w="450px"
            h="725px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box>
              <Text fontSize="36px" fontWeight="bold">
                Create Account
              </Text>

              <FormInput
                label="Email"
                register={register('email', {
                  required: 'Email is required.',
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: 'Please enter a valid email address.'
                  }
                })}
                error={errors.email}
                disabled
                type="email"
                autoComplete="email"
              />

              <FormInput
                label="Full Name"
                register={register('name', {
                  required: 'Full name is required.',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters long.'
                  }
                })}
                error={errors.name}
                placeholder="Enter your full name"
                disabled={isSubmitting}
              />

              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Password
                </Text>
                <Flex alignItems="center" position="relative">
                  <Input
                    fontSize="16px"
                    id="password"
                    placeholder="Create a password"
                    _placeholder={{ color: 'neutrals.300' }}
                    _focus={{
                      boxShadow: 'none',
                      outline: 'none',
                      borderColor: errors.password ? 'error.700' : 'neutrals.400'
                    }}
                    borderColor={errors.password ? 'error.700' : 'neutrals.300'}
                    variant="outline"
                    type={showPassword ? 'text' : 'password'}
                    p="12px 16px"
                    h="48px"
                    autoComplete="new-password"
                    borderRadius="8px"
                    disabled={isSubmitting}
                    {...register('password', {
                      required: 'Password is required.',
                      minLength: {
                        value: PASSWORD_REQUIREMENTS.MIN_LENGTH,
                        message: `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters.`
                      },
                      maxLength: {
                        value: PASSWORD_REQUIREMENTS.MAX_LENGTH,
                        message: `Password must be at most ${PASSWORD_REQUIREMENTS.MAX_LENGTH} characters.`
                      },
                      pattern: {
                        value: PASSWORD_REQUIREMENTS.PATTERN,
                        message:
                          'Password must contain at least one uppercase letter, one lowercase letter, and one number.'
                      },
                      onChange: handlePasswordChange
                    })}
                  />
                  <Box
                    onClick={() => setShowPassword(!showPassword)}
                    position="absolute"
                    right="10px"
                    cursor="pointer"
                  >
                    {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                  </Box>
                </Flex>
                {password && <PasswordStrengthIndicator strength={passwordStrength} />}
                {errors.password && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.password.message}
                  </Text>
                )}
              </Box>

              <FormInput
                label="Confirm Password"
                register={register('confirmPassword', {
                  required: 'Please confirm your password.',
                  validate: (value) => value === password || 'The passwords do not match.'
                })}
                error={errors.confirmPassword}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={isSubmitting}
                rightElement={
                  <Box
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    position="absolute"
                    right="10px"
                    cursor="pointer"
                  >
                    {showConfirmPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                  </Box>
                }
              />

              <Box mt="20px">
                <Flex alignItems="center">
                  <Checkbox
                    cursor="pointer"
                    id="agree-to-terms"
                    checked={agreeToTerms}
                    {...register('agreeToTerms', {
                      required: 'You must agree to the terms and conditions.'
                    })}
                  >
                    <Text fontSize="16px" color="neutrals.500">
                      I agree to the{' '}
                      <ChakraLink
                        href="https://splashy-leopon-ea4.notion.site/terms-of-use"
                        target="_blank"
                        color="neutrals.500"
                        textDecoration="underline"
                      >
                        Terms and Conditions
                      </ChakraLink>
                    </Text>
                  </Checkbox>
                </Flex>
                {errors.agreeToTerms && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.agreeToTerms.message}
                  </Text>
                )}
              </Box>

              {submitError && (
                <Text fontSize="14px" color="error.700" mt="12px" ml="5px">
                  {submitError}
                </Text>
              )}

              <Box mt="20px">
                <Button
                  bg="neutrals.1000"
                  w="100%"
                  h="60px"
                  color="neutrals.0"
                  borderRadius="8px"
                  type="submit"
                  fontSize="20px"
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ bg: 'primary.800' }}
                  disabled={!agreeToTerms || isSubmitting}
                  loading={isSubmitting}
                >
                  Create Account
                </Button>
              </Box>
            </Box>
          </Fieldset.Root>
        </form>
      </Flex>
    </Container>
  );
}
